import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { FileText, AlertTriangle, CheckCircle, ArrowLeft, ArrowRight, Info } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { AnalysisProgress } from '@/components/upload/AnalysisProgress';
import { useAnalysisStatus } from '@/hooks/useAnalysisStatus';
import { PaymentModal } from '@/components/payment/PaymentModal';
import { useAuth } from '@/hooks/useAuth';
import { useAnalytics } from '@/hooks/useAnalytics';
import { Modal, ModalFooter } from '@/components/ui/Modal';
import { DPEAPI } from '@/services/api';
import { metaPixel } from '@/utils/meta-pixel';
import {
  isDPEPre2026,
  isChauffageElectriqueDetected,
  calculateEtiquetteDPE,
  calculateDPEProjection,
  calculateClasseEnergetiquePure,
  calculateClasseClimat,
  getClasseFinaleOfficielle
} from '@/utils/dpe-projections';

export const AnalysisPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const analytics = useAnalytics();

  // Utiliser le hook pour suivre le statut de l'analyse
  const {
    status,
    progress,
    data,
    error,
    questionnaireId,
    retry,
    cancel
  } = useAnalysisStatus(id || '');

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [isGeneratingQuestions, setIsGeneratingQuestions] = useState(false);
  const [questionsError, setQuestionsError] = useState<string | null>(null);
  
  // Fonction pour générer les questions contextuelles
  const handleGenerateQuestions = async () => {
    if (!id) return;
    
    try {
      setIsGeneratingQuestions(true);
      setQuestionsError(null);
      
      console.log('Génération des questions pour DPE:', id);
      
      // Appeler l'API pour générer les questions
      const response = await DPEAPI.getQuestions(id);
      
      console.log('Questions générées:', response);
      
      if (response.success && response.session_id) {
        // Track questions generation
        analytics.trackEvent({
          category: 'analysis',
          action: 'questions_generated',
          label: id,
          sessionId: response.session_id
        });
        
        // Track with Meta Pixel
        metaPixel.trackAnalysisComplete(id, data?.energy_class || 'D', true);
        
        // Naviguer vers la page des questions
        navigate(`/analysis/${id}/questions/${response.session_id}`);
      } else {
        throw new Error(response.message || 'Erreur lors de la génération des questions');
      }
    } catch (error: any) {
      console.error('Erreur génération questions:', error);
      setQuestionsError(error.message || 'Erreur lors de la génération des questions');
      
      // Track error
      analytics.trackEvent({
        category: 'analysis',
        action: 'questions_error',
        label: id,
        error: error.message
      });
    } finally {
      setIsGeneratingQuestions(false);
    }
  };
  
  // Show error modal when error occurs
  useEffect(() => {
    if (status === 'error' && error) {
      // Track analysis error
      analytics.trackEvent({
        category: 'analysis',
        action: 'error',
        label: id,
        error: error
      });
      
      // Check if error is related to credits
      if (error.includes('crédit') || error.includes('paiement')) {
        setShowPaymentModal(true);
      } else {
        setShowErrorModal(true);
      }
    }
  }, [status, error]);
  
  // Handle cancel
  const handleCancel = () => {
    // Appeler la fonction d'annulation
    cancel();
    navigate('/dashboard');
  };
  
  return (
    <div className="max-w-4xl mx-auto">
      <Helmet>
        <title>Analyse DPE en cours - DPEWizard</title>
        <meta name="description" content="Analyse DPE en cours de traitement. Suivez la progression de votre analyse énergétique en temps réel." />
      </Helmet>
      
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate('/dashboard')}
          icon={<ArrowLeft className="w-4 h-4 mr-2" />}
        >
          Retour au tableau de bord
        </Button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Analyse DPE en cours
        </h1>
        <p className="text-gray-600">
          Veuillez patienter pendant que nous analysons votre DPE
        </p>
      </div>
      
      {/* Interface simplifiée - une seule colonne */}
      <div className="space-y-6">
        {/* Progression ou carte de succès */}
        {status === 'completed' ? (
          <Card key="analysis-complete" className="border-2 border-green-500 bg-green-50">
            <CardContent className="p-8 text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Analyse terminée avec succès !
              </h2>
              <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
                Données DPE récupérées et analysées. Passez maintenant aux questions contextuelles
                pour obtenir des recommandations personnalisées adaptées à votre situation.
              </p>
              <Button
                variant="primary"
                size="xl"
                onClick={handleGenerateQuestions}
                loading={isGeneratingQuestions}
                disabled={isGeneratingQuestions}
                className="px-12 py-4 text-lg font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200"
              >
                {isGeneratingQuestions ? 'Génération des questions...' : 'Continuer vers les questions'}
                <ArrowRight className="w-6 h-6 ml-3" />
              </Button>

              {questionnaireId && (
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => navigate(`/analysis/${id}/results`)}
                  className="mt-4"
                >
                  Voir les résultats précédents
                </Button>
              )}

              {questionsError && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-3 text-red-600">
                    <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                    <p className="text-sm">{questionsError}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : (
          <AnalysisProgress
            key="analysis-progress"
            status={status}
            progress={progress}
            errorMessage={error || undefined}
          />
        )}

        {/* Résumé DPE (optionnel, affiché seulement si analyse terminée) */}
        {status === 'completed' && data && (() => {
          // Calculer la consommation et la classe avec le coefficient 2026
          const isPre2026 = isDPEPre2026(data.date_etablissement_dpe);
          const rawConsumption = data.primary_energy_consumption || data.conso_5_usages_par_m2_ep || 0;

          // Si DPE pré-2026, calculer la projection 2026 pour obtenir la consommation corrigée
          let displayConsumption = rawConsumption;
          let showCoefficientMessage = false;

          if (isPre2026 && rawConsumption > 0) {
            try {
              // Utiliser calculateDPEProjection qui gère les installations mixtes
              const projection = calculateDPEProjection(data);
              console.log('📊 DEBUG: Projection object received:', projection);

              if (projection && projection.consoPar_m2_2026 > 0) {
                displayConsumption = Math.round(projection.consoPar_m2_2026);
                showCoefficientMessage = true;
                console.log('📊 Applying 2026 coefficient in summary:', {
                  raw: rawConsumption,
                  projected: displayConsumption,
                  isMixed: projection.isMixedInstallation,
                  mixedDetails: projection.mixedInstallationDetails
                });
              }
            } catch (error) {
              console.error('Error calculating DPE projection:', error);
              // Fallback à l'ancienne méthode en cas d'erreur
              const isElectric = isChauffageElectriqueDetected(data);
              if (isElectric) {
                displayConsumption = Math.round(rawConsumption * (1.9 / 2.3));
                showCoefficientMessage = true;
              }
            }
          }

          // Calculer les classes réelles
          const displayGES = data.greenhouse_gas_emissions || data.emission_ges_5_usages_par_m2 || 0;
          const classeEnergie = calculateClasseEnergetiquePure(displayConsumption, data.living_area);
          const classeClimat = calculateClasseClimat(displayGES);
          const classeFinale = getClasseFinaleOfficielle(classeEnergie, classeClimat);
          const classesAreDifferent = classeEnergie !== classeClimat;

          return (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-blue-500" />
                  Résumé des données DPE
                </CardTitle>
                <CardDescription>
                  Données officielles récupérées depuis l'API ADEME
                </CardDescription>
              </CardHeader>

              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="text-sm text-gray-500 mb-2">Classe énergétique</div>
                    <Badge
                      energyClass={classeEnergie as any}
                      size="lg"
                      isWorst={classeEnergie === classeFinale}
                    />
                  </div>

                  <div className="text-center">
                    <div className="text-sm text-gray-500 mb-2">Consommation</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {displayConsumption}
                    </div>
                    <div className="text-xs text-gray-500">kWh/m²/an</div>
                  </div>

                  <div className="text-center">
                    <div className="text-sm text-gray-500 mb-2">Classe climat</div>
                    <Badge
                      energyClass={classeClimat as any}
                      size="lg"
                      isWorst={classeClimat === classeFinale}
                    />
                  </div>

                  <div className="text-center">
                    <div className="text-sm text-gray-500 mb-2">Émissions GES</div>
                    <div className="text-lg font-semibold text-gray-900">
                      {displayGES}
                    </div>
                    <div className="text-xs text-gray-500">kgCO₂/m²/an</div>
                  </div>
                </div>

                {/* Classe finale DPE officielle */}
                <div className="mt-6 p-4 bg-gray-50 border-2 border-gray-300 rounded-lg">
                  <div className="flex items-center justify-center gap-3">
                    <span className="text-sm font-medium text-gray-700">
                      Classe DPE officielle (retenue sur le document) :
                    </span>
                    <Badge
                      energyClass={classeFinale as any}
                      size="lg"
                      isWorst={true}
                    />
                  </div>
                </div>

                {/* Message pédagogique sur les deux classes (affiché uniquement si différentes) */}
                {classesAreDifferent && (
                  <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <Info className="w-5 h-5 text-green-700 mt-0.5 flex-shrink-0" />
                      <div className="text-sm text-green-800">
                        <p className="font-semibold mb-2">
                          Pourquoi deux classes différentes ?
                        </p>
                        <p className="mb-2">
                          Votre logement possède <strong>deux classes distinctes</strong> :
                        </p>
                        <ul className="list-disc ml-5 space-y-1 mb-2">
                          <li>
                            <strong>Classe énergétique {classeEnergie}</strong> : calculée à partir de la consommation énergétique ({displayConsumption} kWh/m²/an)
                          </li>
                          <li>
                            <strong>Classe climat {classeClimat}</strong> : calculée à partir des émissions de gaz à effet de serre ({displayGES} kg CO₂/m²/an)
                          </li>
                        </ul>
                        <p className="font-semibold">
                          Sur le DPE officiel, c'est la <u>moins bonne des deux classes</u> qui est retenue et affichée en gros : Classe <strong>{classeFinale}</strong>
                          <span className="ml-1">(identifiée par la bordure rouge)</span>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Message pour le coefficient 2026 appliqué */}
                {showCoefficientMessage && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-800">
                        <strong>DPE établi avant 2026 :</strong> La consommation et la classe énergétique affichées intègrent déjà le nouveau coefficient électrique (1.9 au lieu de 2.3) appliqué depuis le 1er janvier 2026.
                      </p>
                    </div>
                  </div>
                )}

                {/* Message pour les petites surfaces */}
                {data.living_area && data.living_area < 40 && (
                  <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                      <p className="text-sm text-blue-800">
                        <strong>Petite surface ({data.living_area} m²) :</strong> Les lettres affichées tiennent compte de l'évolution des règles en matière de petit logement.
                        Elles peuvent différer de celles indiquées sur votre DPE papier si celui-ci date d'avant juillet 2024.
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })()}
      </div>
      
      {/* Error Modal */}
      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        title="Erreur lors de l'analyse"
      >
        <div className="p-4 bg-red-50 rounded-lg mb-6">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="font-medium text-red-800 mb-1">L'analyse a échoué</h4>
              <p className="text-sm text-red-700">
                {error || "Une erreur est survenue lors de l'analyse de votre DPE."}
              </p>
              {data?.numero_dpe && (
                <div className="mt-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-1">Numéro DPE</h4>
                  <p className="text-gray-900 font-mono text-sm">{data.numero_dpe}</p>
                </div>
              )}
            </div>
          </div>
        </div>
        
        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
          >
            Retour au tableau de bord
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              setShowErrorModal(false);
              retry();
            }}
          >
            Réessayer
          </Button>
        </ModalFooter>
      </Modal>
      
      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        creditsAmount={1}
        price={9.90}
        dpeId={id}
        onSuccess={() => {
          setShowPaymentModal(false);
          retry();
        }}
      />
    </div>
  );
};

export default AnalysisPage;